import type { TrainingGoalPeriod } from '../types'

/** Weekly → yearly equivalence used in copy: 3 per week = 156 per year. */
export const WEEKS_PER_YEAR = 52
const DAY_MS = 86_400_000

function dayKey(date: Date): number {
  return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate())
}

function daysInYear(year: number): number {
  return (Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1)) / DAY_MS
}

export function equivalentYearlyTarget(weekly: number): number {
  return weekly * WEEKS_PER_YEAR
}

export function currentPeriod(periods: TrainingGoalPeriod[]): TrainingGoalPeriod {
  return periods.find((period) => !period.effectiveTo) ?? periods[periods.length - 1]
}

/**
 * Yearly target as the proportional sum of every goal period inside the year.
 * A single full-year period of 3/week gives exactly 156. Periods are read as stored,
 * so a change mid-year never rewrites the target already accrued before it.
 */
export function yearlyTarget(periods: TrainingGoalPeriod[], year: number): number {
  const yearStart = Date.UTC(year, 0, 1)
  const yearEnd = Date.UTC(year + 1, 0, 1)
  const total = daysInYear(year)
  const sum = periods.reduce((acc, period) => {
    const from = Math.max(dayKey(period.effectiveFrom), yearStart)
    const to = Math.min(
      period.effectiveTo ? dayKey(period.effectiveTo) + DAY_MS : yearEnd,
      yearEnd
    )
    if (to <= from) return acc
    const days = (to - from) / DAY_MS
    return acc + (period.weeklyTarget * days * WEEKS_PER_YEAR) / total
  }, 0)
  return Math.round(sum)
}

/**
 * Applies a new weekly goal from `on` onwards: the open period ends the day before and
 * a new open period starts on `on`. A change on the open period's own start day only
 * corrects that period, so no zero-length periods are created.
 */
export function changeWeeklyTarget(
  periods: TrainingGoalPeriod[],
  weekly: number,
  on: Date
): TrainingGoalPeriod[] {
  const open = currentPeriod(periods)
  if (!open || open.weeklyTarget === weekly) return periods
  const start = new Date(on.getFullYear(), on.getMonth(), on.getDate())
  if (dayKey(open.effectiveFrom) >= dayKey(start)) {
    return periods.map((period) =>
      period.id === open.id ? { ...period, weeklyTarget: weekly } : period
    )
  }
  const previousDay = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1)
  return [
    ...periods.map((period) =>
      period.id === open.id ? { ...period, effectiveTo: previousDay } : period
    ),
    { id: `period-${dayKey(start)}`, weeklyTarget: weekly, effectiveFrom: start },
  ]
}
